import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTradereportingV1CaisReportQueryServiceCaisReportGet } from '@interxfi/api-client/generated/hooks/ixc-tradereporting-v1-cais-report-query-service-cais-report-get';
import { useIxfReportsV1ReportCommandServiceReportDownload } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-command-service-report-download';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import ReportsIcon from '@interxfi/design-system/icons/react/icon-reports';
import { type IxcTradereportingV1CaisReportState, IxcTradereportingV1CaisReportStatus } from '@interxfi/types';
import { downloadFromUrl } from '../../../../../../../../../../features/document/utils';
import { CaisReportEventDrawer, CaisReportEventSummary } from '../../../../../../../../../../features/trade-reporting/cais/cais-report-event';
import { getTradeReportingCaisReportStateListItems } from '../../../../../../../../../../features/trade-reporting/cais/cais-report-list-items';
import { getCaisReportDetailPath } from '../../../../../../../../../../features/trade-reporting/cais/cais-report-routing';
import { CaisReportStatusPill } from '../../../../../../../../../../features/trade-reporting/cais/cais-report-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/reporting/cais/_cais/$reportId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `cais-report-${params.reportId}`,
        label: 'CAIS Report',
        to: getCaisReportDetailPath(params.reportId),
        suffix: (
          <CopyButton content={params.reportId} label="Copy CAIS Report ID" tooltip={{ copy: params.reportId, copied: 'Copied CAIS Report ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('CAIS Report'),
  component: CaisReportDetail,
});

function getListItems(data: IxcTradereportingV1CaisReportState | undefined) {
  const listItems = getTradeReportingCaisReportStateListItems(data);

  return {
    general: {
      left: [listItems.data.reportDate, listItems.data.crdNumber, listItems.data.totalAccounts, listItems.data.fileKeys] satisfies ReadonlyListItem[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.reportId,
        listItems.data.tidsReportId,
      ] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(
  reportId: string,
  data: IxcTradereportingV1CaisReportState | undefined,
  downloadReport: VoidFunction,
  downloadTidsReport: VoidFunction,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: CaisReportEventDrawer,
        props: { request: { reportId } },
      },
    },
    actions: [
      ...(data?.status === IxcTradereportingV1CaisReportStatus.Generated || data?.status === IxcTradereportingV1CaisReportStatus.Submitted
        ? [
            {
              id: 'download',
              icon: <DownloadIcon aria-hidden />,
              label: 'Download',
              disabled: isDownloading,
              onClick: downloadReport,
            },
          ]
        : []),
      ...(data?.data?.tidsReportId
        ? [
            {
              id: 'downloadTidsReport',
              icon: <ReportsIcon aria-hidden />,
              label: 'Download TIDS Report',
              disabled: isDownloading,
              onClick: downloadTidsReport,
            },
          ]
        : []),
    ],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load CAIS report', 'view this CAIS report');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to download report', 'download this report');

function CaisReportDetail() {
  const { reportId } = Route.useParams();
  const request = { reportId } as const;
  const { data, error, isPending } = useIxcTradereportingV1CaisReportQueryServiceCaisReportGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const { mutateAsync: downloadReport, error: downloadError, isPending: isDownloading } = useIxfReportsV1ReportCommandServiceReportDownload();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const handleDownloadReport = useCallback(
    async (id: string | undefined) => {
      if (!id) {
        return;
      }

      try {
        const res = await downloadReport({ reportId: id });

        if (res?.url) {
          downloadFromUrl(res.url);
        }
      } catch {}
    },
    [downloadReport],
  );

  const listItems = useMemo(() => getListItems(data?.caisReport), [data?.caisReport]);
  const menuItems = useMemo(
    () =>
      getMenuItems(
        reportId,
        data?.caisReport,
        () => handleDownloadReport(reportId),
        () => handleDownloadReport(data?.caisReport?.data?.tidsReportId),
        isDownloading,
      ),
    [reportId, data?.caisReport?.status, handleDownloadReport, isDownloading, data?.caisReport],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <CaisReportStatusPill isPending={showPending} size="large" status={data?.caisReport?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <CaisReportEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
